import { Fragment, type ReactNode } from "react";
import { SearchText } from "./SearchText";
import { definedTermTextSegments, type DefinedTermMap as DefinedTermMapType } from "../domain/definedTerms";
import type { RichTextDoc, RichTextNode } from "../domain/richText";
export type { DefinedTerm, DefinedTermMap } from "../domain/definedTerms";

/**
 * Read-only renderer for the TipTap/ProseMirror docs stored on sections and
 * definitions. Handles common nodes/marks (incl. amendment deletion/insertion
 * marks); unknown nodes fall back to rendering their children. Defined terms
 * are highlighted inline and, when an interaction is supplied, drive a popover.
 */

export type TermInteraction = {
  show: (def: { term: string; text: string }, el: HTMLElement) => void;
  hide: () => void;
};

type Opts = {
  searchQuery?: string;
  terms?: DefinedTermMapType;
  interaction?: TermInteraction;
  legal?: boolean; // use serif legal body styling on paragraphs
};

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
type HeadingTag = `h${HeadingLevel}`;

const DEFAULT_HEADING_LEVEL: HeadingLevel = 2;
const HEADING_TAGS: Record<HeadingLevel, HeadingTag> = {
  1: "h1",
  2: "h2",
  3: "h3",
  4: "h4",
  5: "h5",
  6: "h6",
};

const isHeadingLevel = (value: unknown): value is HeadingLevel =>
  value === 1 || value === 2 || value === 3 || value === 4 || value === 5 || value === 6;

const headingLevelFromAttrs = (attrs: RichTextNode["attrs"]): HeadingLevel => {
  const value = attrs?.["level"];
  return isHeadingLevel(value) ? value : DEFAULT_HEADING_LEVEL;
};

const headingTagFor = (level: HeadingLevel): HeadingTag => HEADING_TAGS[level];
const INLINE_LINE_BREAK_RE = /\r\n?|\n/;

export function RichText({
  doc,
  terms,
  interaction,
  legal,
  searchQuery,
}: {
  doc: RichTextDoc;
  terms?: DefinedTermMapType;
  interaction?: TermInteraction;
  legal?: boolean;
  searchQuery?: string;
}) {
  const opts: Opts = { terms, interaction, legal, searchQuery };
  return <>{(doc.content ?? []).map((n, i) => renderNode(n, `${i}`, opts))}</>;
}

function renderNode(node: RichTextNode, key: string, opts: Opts): ReactNode {
  const children = (node.content ?? []).map((c, i) => renderNode(c, `${key}.${i}`, opts));
  const pClass = opts.legal ? "ls-body my-3" : "my-2 leading-relaxed";

  switch (node.type) {
    case "paragraph":
      return <p key={key} className={pClass}>{children}</p>;
    case "heading": {
      const Tag = headingTagFor(headingLevelFromAttrs(node.attrs));
      return <Tag key={key} className="mb-2 mt-4 font-semibold">{children}</Tag>;
    }
    case "bulletList":
      return <ul key={key} className="my-2 list-disc pl-6">{children}</ul>;
    case "orderedList":
      return <ol key={key} start={typeof node.attrs?.["start"] === "number" ? node.attrs["start"] : undefined} className="my-2 list-decimal pl-6">{children}</ol>;
    case "listItem":
      return <li key={key} className={opts.legal ? "ls-body" : ""}>{children}</li>;
    case "blockquote":
      return <blockquote key={key} className="border-l-4 border-slate-300 pl-4 italic">{children}</blockquote>;
    case "hardBreak":
      return <br key={key} />;
    case "image": {
      const src = String(node.attrs?.["src"] ?? "");
      const alt = node.attrs?.["alt"] != null ? String(node.attrs["alt"]) : undefined;
      return src === "" ? null : <img key={key} src={src} alt={alt} className="my-3 max-w-full rounded" />;
    }
    case "table":
      return (
        <div key={key} className="my-3 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <tbody>{children}</tbody>
          </table>
        </div>
      );
    case "tableRow":
      return <tr key={key}>{children}</tr>;
    case "tableHeader":
      return <th key={key} colSpan={attrNum(node, "colspan")} rowSpan={attrNum(node, "rowspan")} className="border border-slate-300 bg-slate-50 px-2 py-1 text-left font-semibold">{children}</th>;
    case "tableCell":
      return <td key={key} colSpan={attrNum(node, "colspan")} rowSpan={attrNum(node, "rowspan")} className="border border-slate-300 px-2 py-1 align-top">{children}</td>;
    case "text":
      return renderText(node, key, opts);
    default:
      return <Fragment key={key}>{children}</Fragment>;
  }
}

function renderText(node: RichTextNode, key: string, opts: Opts): ReactNode {
  let content: ReactNode = renderInlineText(node.text ?? "", key, opts);
  for (const mark of node.marks ?? []) {
    switch (mark.type) {
      case "bold":
        content = <strong>{content}</strong>;
        break;
      case "italic":
        content = <em>{content}</em>;
        break;
      case "strike":
      case "deletion":
        content = <del className="ls-del">{content}</del>;
        break;
      case "insertion":
        content = <strong className="ls-ins">{content}</strong>;
        break;
      case "highlight":
        content = <mark>{content}</mark>;
        break;
    }
  }
  return <Fragment key={key}>{content}</Fragment>;
}

function renderInlineText(text: string, key: string, opts: Opts): ReactNode {
  if (!INLINE_LINE_BREAK_RE.test(text)) return highlightTerms(text, opts);

  const parts = text.split(INLINE_LINE_BREAK_RE);
  return parts.map((part, index) => (
    <Fragment key={`${key}.line.${index}`}>
      {index > 0 && <br />}
      {highlightTerms(part, opts)}
    </Fragment>
  ));
}

function highlightTerms(text: string, opts: Opts): ReactNode {
  const terms = opts.terms;
  if (terms === undefined) return opts.searchQuery ? <SearchText text={text} query={opts.searchQuery} /> : text;

  const interaction = opts.interaction;
  const segments = definedTermTextSegments(text, terms);
  if (segments.length === 0) return "";
  if (segments.length === 1 && segments[0]?.kind === "text") return opts.searchQuery ? <SearchText text={text} query={opts.searchQuery} /> : segments[0].text;

  return segments.map((segment, index) => {
    if (segment.kind === "text") return opts.searchQuery ? <SearchText key={index} text={segment.text} query={opts.searchQuery} /> : segment.text;
    return (
      <span
        key={`${index}.${segment.definition.term}`}
        tabIndex={0}
        className="ls-term"
        title={interaction ? undefined : segment.definition.text}
        onMouseEnter={interaction ? (e) => interaction.show(segment.definition, e.currentTarget) : undefined}
        onMouseLeave={interaction ? interaction.hide : undefined}
        onFocus={interaction ? (e) => interaction.show(segment.definition, e.currentTarget) : undefined}
        onBlur={interaction ? interaction.hide : undefined}
      >
        {segment.text}
      </span>
    );
  });
}

const attrNum = (node: RichTextNode, key: string): number | undefined => {
  const v = node.attrs?.[key];
  return typeof v === "number" && v > 1 ? v : undefined;
};
